import type { ComponentChildren } from 'preact';
import { useRef } from 'preact/hooks';
import {
  BOT_KINDS,
  MAX_MAP_SIZE,
  SPECIAL_BUILD_MIN_PLAYERS,
  MIN_MAP_SIZE,
  RESOURCES,
  autoMapSize,
  landmassForSize,
  type BoardLayout,
  type BotKind,
  type HouseRules,
  type Resource,
  type Resources,
} from '../engine';
import { TURN_TIMER_CHOICES, type GameSettings, type TurnSeconds } from '../shared/protocol';
import { useSeatColor } from './colors';
import { BOT_LABELS, PLAYER_PALETTE, RESOURCE_COLORS, RESOURCE_LABELS, RESOURCE_TEXT, paletteColor } from './theme';

export function ResourceChip({
  resource,
  count,
  gained = 0,
  gainKey = 0,
}: {
  resource: Resource;
  count?: number;
  /** Shows a floating "+N" (or "−N" for a loss); bump `gainKey` to replay it. */
  gained?: number;
  gainKey?: number;
}) {
  return (
    <span class="chip" style={{ background: RESOURCE_COLORS[resource], color: RESOURCE_TEXT[resource] }} title={RESOURCE_LABELS[resource]}>
      <span class="chip-label">{RESOURCE_LABELS[resource]}</span>
      {count !== undefined && <span class="chip-count">{count}</span>}
      {gained !== 0 && (
        <span class={`gain${gained < 0 ? ' loss' : ''}`} key={gainKey} aria-hidden="true">
          {gained > 0 ? `+${gained}` : `−${-gained}`}
        </span>
      )}
    </span>
  );
}

export function ResourceList({ resources }: { resources: Resources }) {
  const shown = RESOURCES.filter((r) => resources[r] > 0);
  if (shown.length === 0) return <span class="muted">nothing</span>;
  return (
    <span class="chips">
      {shown.map((r) => (
        <ResourceChip key={r} resource={r} count={resources[r]} />
      ))}
    </span>
  );
}

/** Swatch and name; `tag` (e.g. the CPU badge) sits between them so names line up down a list. */
export function PlayerName({ seat, names, tag }: { seat: number; names: readonly string[]; tag?: ComponentChildren }) {
  const seatColor = useSeatColor();
  return (
    <span class="player-name">
      <span class="swatch" style={{ background: seatColor(seat) }} />
      {tag}
      <span class="name-text">{names[seat] ?? `Player ${seat + 1}`}</span>
    </span>
  );
}

/** +/- steppers for a Resources bundle, each capped by `max` when given. */
export function ResourcePicker({
  value,
  onChange,
  max,
  maxTotal,
  held,
  testid,
}: {
  value: Resources;
  onChange: (next: Resources) => void;
  max?: Resources;
  /** Caps the sum across all resources (e.g. exactly what a discard owes). */
  maxTotal?: number;
  /** Shows how many of each the player holds on its chip. */
  held?: Resources;
  testid?: string;
}) {
  const full = maxTotal !== undefined && RESOURCES.reduce((n, r) => n + value[r], 0) >= maxTotal;
  return (
    <div class="picker" data-testid={testid}>
      {RESOURCES.map((r) => (
        <div class="picker-row" key={r}>
          <ResourceChip resource={r} {...(held ? { count: held[r] } : {})} />
          <button
            type="button"
            class="step"
            aria-label={`less ${r}`}
            disabled={value[r] === 0}
            onClick={() => onChange({ ...value, [r]: value[r] - 1 })}
          >
            −
          </button>
          <span class="picker-count" data-testid={testid ? `${testid}-${r}` : undefined}>
            {value[r]}
          </span>
          <button
            type="button"
            class="step"
            aria-label={`more ${r}`}
            disabled={full || (max !== undefined && value[r] >= max[r])}
            onClick={() => onChange({ ...value, [r]: value[r] + 1 })}
          >
            +
          </button>
        </div>
      ))}
    </div>
  );
}

const PIP_LAYOUT: Record<number, [number, number][]> = {
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [2, 0], [0, 2], [2, 2]],
  5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]],
  6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};

export function Die({ value }: { value: number }) {
  return (
    <svg class="die" viewBox="0 0 30 30" aria-label={`die showing ${value}`}>
      <rect x="1" y="1" width="28" height="28" rx="5" fill="#fff" stroke="#1b1b1b" stroke-width="1.5" />
      {(PIP_LAYOUT[value] ?? []).map(([cx, cy], i) => (
        <circle key={i} cx={7 + cx * 8} cy={7 + cy * 8} r="2.6" fill="#1b1b1b" />
      ))}
    </svg>
  );
}

export function TimerSelect({ value, onChange }: { value: TurnSeconds; onChange: (v: TurnSeconds) => void }) {
  return (
    <select
      aria-label="Turn timer"
      data-testid="turn-timer"
      value={value ?? ''}
      onChange={(e) => {
        const raw = (e.target as HTMLSelectElement).value;
        onChange(raw === '' ? null : (Number(raw) as TurnSeconds));
      }}
    >
      {TURN_TIMER_CHOICES.map((c) => (
        <option key={c ?? 'off'} value={c ?? ''}>
          {c === null ? 'No turn timer' : c < 60 ? `${c} sec turns` : `${c / 60} min turns`}
        </option>
      ))}
    </select>
  );
}

export const HOUSE_RULE_INFO: readonly { key: keyof HouseRules; label: string; detail: string }[] = [
  {
    key: 'robberWaits',
    label: 'Robber waits',
    detail: "No robber until someone builds a third settlement or a city. 7s are re-rolled and Knights can't be played.",
  },
  { key: 'revealVictoryPoints', label: 'Reveal Victory Point cards', detail: 'Shown to everyone and scored as soon as they are bought.' },
  { key: 'islands', label: 'Random islands', detail: 'Land is scattered into one extra ring of sea, making islands and lakes.' },
  { key: 'scrambleNumbers', label: 'Scrambled numbers', detail: 'Number tokens are shuffled freely, so 6s and 8s can touch.' },
  {
    key: 'specialBuild',
    label: 'Build between turns',
    detail: '5+ players: anyone can ask to build when the current turn ends, in play order. No trading then.',
  },
];

/** The fixed beginner layout only exists for the base island. */
export function beginnerAvailable(playerCount: number, settings: Pick<GameSettings, 'rules' | 'mapSize'>): boolean {
  const size = settings.mapSize === 'auto' ? autoMapSize(playerCount) : settings.mapSize;
  return size <= 5 && !settings.rules.islands;
}

const MAP_SIZE_NAMES: Record<number, string> = { 5: 'Small', 6: 'Medium', 7: 'Large', 8: 'Huge', 9: 'Giant', 10: 'Enormous' };
const VP_CHOICES = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 18, 20, 25];

/**
 * Board, timer, and house rules. Editable for whoever sets up the game (the
 * online host, or anyone in hot-seat); read-only for everyone else, so the
 * whole table sees what's been chosen.
 */
export function SettingsForm({
  value,
  onChange,
  playerCount,
  seatNames,
  readOnly = false,
}: {
  value: GameSettings;
  onChange?: (next: GameSettings) => void;
  playerCount: number;
  /** Names for the "who goes first" choice. */
  seatNames: readonly string[];
  readOnly?: boolean;
}) {
  const set = (patch: Partial<GameSettings>) => onChange?.({ ...value, ...patch });
  const beginner = beginnerAvailable(playerCount, value);
  const hexes = (size: number) => landmassForSize(size).length;
  const auto = autoMapSize(playerCount);
  const sizes = Array.from({ length: MAX_MAP_SIZE - MIN_MAP_SIZE + 1 }, (_, i) => MIN_MAP_SIZE + i);
  const vpChoices = VP_CHOICES.includes(value.victoryPoints) ? VP_CHOICES : [...VP_CHOICES, value.victoryPoints].sort((a, b) => a - b);
  const layout: BoardLayout = beginner ? value.layout : 'random';
  // A chosen seat that's since left shows (and plays) as a roll.
  const first = typeof value.firstPlayer === 'number' && value.firstPlayer < seatNames.length ? value.firstPlayer : 'roll';
  return (
    <fieldset class="settings" disabled={readOnly} data-testid="settings">
      <legend class="sr-only">Game settings</legend>
      <div class="settings-row">
        <label>
          <span>Board</span>
          <select
            data-testid="layout"
            value={layout}
            onChange={(e) => set({ layout: (e.target as HTMLSelectElement).value as BoardLayout })}
          >
            <option value="random">Random</option>
            <option value="fixed" disabled={!beginner}>
              Beginner (fixed){beginner ? '' : ' — small map, no islands'}
            </option>
          </select>
        </label>
        <label>
          <span>Map size</span>
          <select
            data-testid="map-size"
            value={String(value.mapSize)}
            onChange={(e) => {
              const raw = (e.target as HTMLSelectElement).value;
              set({ mapSize: raw === 'auto' ? 'auto' : Number(raw) });
            }}
          >
            <option value="auto">
              Auto ({hexes(auto)} hexes for {playerCount})
            </option>
            {sizes.map((n) => (
              <option key={n} value={String(n)}>
                {MAP_SIZE_NAMES[n]} ({hexes(n)} hexes)
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Points to win</span>
          <select
            data-testid="victory-points"
            value={String(value.victoryPoints)}
            onChange={(e) => set({ victoryPoints: Number((e.target as HTMLSelectElement).value) })}
          >
            {vpChoices.map((n) => (
              <option key={n} value={String(n)}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Timer</span>
          <TimerSelect value={value.turnSeconds} onChange={(turnSeconds) => set({ turnSeconds })} />
        </label>
        <label>
          <span>Goes first</span>
          <select
            data-testid="first-player"
            value={first === 'roll' ? 'roll' : String(first)}
            onChange={(e) => {
              const raw = (e.target as HTMLSelectElement).value;
              set({ firstPlayer: raw === 'roll' ? 'roll' : Number(raw) });
            }}
          >
            <option value="roll">Roll the dice</option>
            {seatNames.map((name, i) => (
              <option key={i} value={String(i)}>
                {name || `Player ${i + 1}`}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div class="rules-list">
        <span class="rules-title">House rules</span>
        {HOUSE_RULE_INFO.map(({ key, label, detail }) => (
          <label class="rule" key={key}>
            <input
              type="checkbox"
              data-testid={`rule-${key}`}
              checked={!!value.rules[key]}
              disabled={key === 'specialBuild' && playerCount < SPECIAL_BUILD_MIN_PLAYERS}
              onChange={(e) => set({ rules: { ...value.rules, [key]: (e.target as HTMLInputElement).checked } })}
            />
            <span>
              <strong>{label}</strong>
              <span class="muted rule-detail">{detail}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Which strategy plays a CPU seat. */
export function BotPicker({ value, onChange, testid, label }: { value: BotKind; onChange: (k: BotKind) => void; testid: string; label: string }) {
  return (
    <select
      class="bot-picker"
      data-testid={testid}
      aria-label={label}
      title={BOT_LABELS[value].detail}
      value={value}
      onChange={(e) => onChange((e.target as HTMLSelectElement).value as BotKind)}
    >
      {BOT_KINDS.map((k) => (
        <option key={k} value={k}>
          {BOT_LABELS[k].label}
        </option>
      ))}
    </select>
  );
}

/** Choose a player colour. Colours other players hold are crossed out. */
export function ColorPicker({
  value,
  taken,
  onChange,
  testid,
  label,
}: {
  value: number;
  taken: readonly number[];
  onChange: (color: number) => void;
  testid: string;
  label: string;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  const current = PLAYER_PALETTE[value];
  return (
    <details class="color-picker" ref={ref} data-testid={testid}>
      <summary aria-label={`${label}: ${current?.name ?? 'colour'}`} title={`Colour: ${current?.name ?? ''} (tap to change)`}>
        <span class="swatch big" style={{ background: paletteColor(value) }} />
      </summary>
      <div class="color-grid" role="listbox" aria-label={label}>
        {PLAYER_PALETTE.map((c, i) => {
          const gone = taken.includes(i);
          return (
            <button
              key={c.name}
              type="button"
              role="option"
              aria-selected={i === value}
              class={`color-option${i === value ? ' chosen' : ''}`}
              style={{ background: c.hex }}
              title={gone ? `${c.name} (taken)` : c.name}
              aria-label={gone ? `${c.name}, taken` : c.name}
              data-testid={`${testid}-${i}`}
              disabled={gone}
              onClick={() => {
                onChange(i);
                if (ref.current) ref.current.open = false;
              }}
            />
          );
        })}
      </div>
    </details>
  );
}

/** Text fields holding a suggested name select it all on focus, so typing replaces it. */
export const selectOnFocus = (e: FocusEvent) => (e.target as HTMLInputElement).select();
